export interface CustomCourseOutlineContext {
  goalTitle?: string | null;
  taskTitle?: string | null;
  abilityTags?: string[] | null;
}

const customCourseOutline = [
  {
    day: 1,
    theme: '起步行动',
    title: (context: NormalizedOutlineContext) => `从“${context.taskTitle}”开始，把“${context.abilityTitle}”落到一件小事`,
  },
  {
    day: 2,
    theme: '身体余量',
    title: () => '看懂身体余量：为什么先留一点力',
  },
  {
    day: 3,
    theme: '安全边界',
    title: () => '先划清安全边界：什么情况要降级或暂停',
  },
  {
    day: 4,
    theme: '起步行动',
    title: (context: NormalizedOutlineContext) => `沿着“${context.abilityTitle}”继续：把行动做得更稳`,
  },
  {
    day: 5,
    theme: '身体余量',
    title: () => '沿着身体余量再深入：什么时候可以多一点',
  },
  {
    day: 6,
    theme: '安全边界',
    title: () => '用安全边界复盘：哪些信号说明该换个版本',
  },
  {
    day: 7,
    theme: '复盘延续',
    title: (context: NormalizedOutlineContext) => `复盘：留下能继续支持“${context.goalTitle}”的最小动作`,
  },
] as const;

interface NormalizedOutlineContext {
  goalTitle: string;
  taskTitle: string;
  abilityTitle: string;
}

function cleanLabel(value: string | null | undefined, fallback: string) {
  const cleaned = value?.trim();
  return cleaned ? cleaned : fallback;
}

function inferAbilityTitle(taskTitle: string | null | undefined, abilityTags: string[] | null | undefined) {
  const abilityTitle = abilityTags?.find((tag) => tag.trim())?.trim();
  if (abilityTitle) return abilityTitle;
  const title = taskTitle ?? '';
  if (title.includes('推墙') || title.includes('俯卧撑')) return '上肢推力';
  if (title.includes('步行') || title.includes('踏步')) return '步行耐力';
  if (title.includes('楼梯') || title.includes('坐站') || title.includes('起身')) return '下肢力量';
  if (title.includes('握力') || title.includes('毛巾')) return '握力';
  if (title.includes('平衡') || title.includes('单脚')) return '平衡';
  if (title.includes('医生') || title.includes('报告') || title.includes('沟通')) return '健康素养';
  return '行动能力';
}

function normalizeOutlineContext(context: CustomCourseOutlineContext = {}): NormalizedOutlineContext {
  return {
    goalTitle: cleanLabel(context.goalTitle, '我的百岁目标'),
    taskTitle: cleanLabel(context.taskTitle, '当前小行动'),
    abilityTitle: inferAbilityTitle(context.taskTitle, context.abilityTags),
  };
}

export function getCustomCourseOutline(dayNumber: number, context?: CustomCourseOutlineContext) {
  const normalizedDay = Math.max(1, Math.trunc(dayNumber || 1));
  const outline = customCourseOutline[(normalizedDay - 1) % customCourseOutline.length] ?? customCourseOutline[0];
  return {
    day: outline.day,
    theme: outline.theme,
    title: outline.title(normalizeOutlineContext(context)),
  };
}

export function getCustomCourseTitle(dayNumber: number, context?: CustomCourseOutlineContext) {
  return getCustomCourseOutline(dayNumber, context).title;
}

export function isLegacyCustomCourseTitle(title?: string | null) {
  const cleaned = title?.trim();
  if (!cleaned) return true;
  return (
    cleaned.startsWith('为什么先从')
    || cleaned.startsWith('为什么当前先服务')
    || cleaned.includes('FHIR')
    || cleaned.includes('血脂')
    || cleaned.includes('肝酶')
    || /^为了.+今天先做/.test(cleaned)
    || cleaned === '先把百岁目标拆成身体能力'
    || cleaned === '肌力：为什么先从轻量动作开始'
  );
}

export function getResolvedCustomCourseTitle(
  dayNumber: number,
  title?: string | null,
  context?: CustomCourseOutlineContext,
) {
  const cleaned = title?.trim();
  if (cleaned && !isLegacyCustomCourseTitle(cleaned)) return cleaned;
  return getCustomCourseTitle(dayNumber, context);
}

export function getCustomCourseTheme(dayNumber: number) {
  return getCustomCourseOutline(dayNumber).theme;
}
